import { Descriptions, Empty, Tag, Typography } from "antd";
import dayjs from "dayjs";
import type {
  OcppMessageEntry,
  OcppMessageView,
} from "../../../services/client/types.gen";
import {
  MESSAGE_TIME_FORMAT,
  MESSAGE_TYPE_COLORS,
  MESSAGE_TYPE_LABELS,
  SENDER_LABELS,
} from "./constants";

const MessageBody = ({ message }: { message: OcppMessageEntry }) => {
  const body =
    message.message_type === "unparsable"
      ? message.raw
      : JSON.stringify(message.payload ?? null, null, 2);

  return (
    <div className="min-w-0">
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <Tag color={MESSAGE_TYPE_COLORS[message.message_type]}>
          {MESSAGE_TYPE_LABELS[message.message_type]}
        </Tag>
        <Typography.Text type="secondary">
          {SENDER_LABELS[message.sender]} ·{" "}
          {dayjs(message.timestamp).format(MESSAGE_TIME_FORMAT)}
        </Typography.Text>
      </div>
      {message.message_type === "call_error" && (
        <Typography.Paragraph type="danger" className="mb-1">
          {message.error_code}: {message.error_description}
        </Typography.Paragraph>
      )}
      <Typography.Paragraph copyable={{ text: body ?? "" }} className="mb-0">
        <pre className="m-0 max-h-96 overflow-auto rounded bg-gray-50 p-3 text-xs whitespace-pre-wrap break-all">
          {body}
        </pre>
      </Typography.Paragraph>
    </div>
  );
};

export const OcppMessageDetails = ({
  message,
}: {
  message: OcppMessageView;
}) => {
  const isRequest = message.message_type === "call";
  const request = isRequest ? message : message.counterpart;
  const response = isRequest ? message.counterpart : message;

  return (
    <div className="space-y-3">
      <Descriptions size="small" column={{ xs: 1, md: 3 }}>
        <Descriptions.Item label="Message id">
          <Typography.Text code copyable>
            {message.message_id ?? "—"}
          </Typography.Text>
        </Descriptions.Item>
        <Descriptions.Item label="Protocol">
          {message.subprotocol}
        </Descriptions.Item>
        <Descriptions.Item label="Connection">
          <Typography.Text code>{message.connection_id}</Typography.Text>
        </Descriptions.Item>
      </Descriptions>

      {message.message_type === "unparsable" ? (
        <MessageBody message={message} />
      ) : (
        <div className="grid gap-4 xl:grid-cols-2">
          {request ? (
            <MessageBody message={request} />
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="The request for this response was not logged"
            />
          )}
          {response ? (
            <MessageBody message={response} />
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No response was logged for this request"
            />
          )}
        </div>
      )}
    </div>
  );
};
